/** Compare portable schema definitions. Database IDs and property order are irrelevant. */
export interface SchemaDiffRow {
  target: string
  operation: 'add' | 'remove' | 'change'
  before: unknown
  after: unknown
}
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const records = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? value.filter(isRecord) : []
const OMIT = new Set(['id', 'schema_id', 'created_at', 'updated_at', 'properties'])
const empty = (value: unknown) =>
  value == null ||
  value === false ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (isRecord(value) && Object.keys(value).length === 0)

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical)
  if (!isRecord(value)) return value
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .filter(key => !OMIT.has(key) && !empty(value[key]))
      .map(key => [key, canonical(value[key])])
  )
}

function compare(target: string, before: unknown, after: unknown): SchemaDiffRow[] {
  if (JSON.stringify(canonical(before)) === JSON.stringify(canonical(after))) return []
  return [
    {
      target,
      operation: before === undefined ? 'add' : after === undefined ? 'remove' : 'change',
      before,
      after,
    },
  ]
}

function typeDiff(section: string, before: unknown, after: unknown): SchemaDiffRow[] {
  const left = new Map(records(before).map(item => [String(item.label), item]))
  const right = new Map(records(after).map(item => [String(item.label), item]))
  return [...new Set([...left.keys(), ...right.keys()])].sort().flatMap(label => {
    const a = left.get(label)
    const b = right.get(label)
    const rows = compare(`${section}.${label}`, a && canonical(a), b && canonical(b))
    const propertiesA = new Map(
      records(a?.properties).map(property => [String(property.name), property])
    )
    const propertiesB = new Map(
      records(b?.properties).map(property => [String(property.name), property])
    )
    for (const name of new Set([...propertiesA.keys(), ...propertiesB.keys()])) {
      rows.push(
        ...compare(`${section}.${label}.${name}`, propertiesA.get(name), propertiesB.get(name))
      )
    }
    return rows
  })
}

function patterns(value: unknown): Map<string, unknown> {
  const result = new Map<string, unknown>()
  if (!Array.isArray(value)) return result
  for (const item of value) {
    const triple = Array.isArray(item)
      ? item
      : isRecord(item)
        ? [item.source_type, item.relationship_type, item.target_type]
        : []
    if (triple.length === 3)
      result.set(triple.join(' → '), {
        pattern: triple.join(' → '),
        description: isRecord(item) ? item.description : undefined,
      })
  }
  return result
}

export function proposalDiff(
  before: Record<string, unknown>,
  after: Record<string, unknown>
): SchemaDiffRow[] {
  const rows = [
    ...typeDiff('entities', before.entity_types, after.entity_types),
    ...typeDiff('relationships', before.relationship_types, after.relationship_types),
    ...compare('name', before.name, after.name),
    ...compare('description', before.description ?? '', after.description ?? ''),
  ]
  for (const [flag, fallback] of [
    ['additional_node_types', false],
    ['additional_relationship_types', false],
    ['additional_patterns', false],
    ['additional_properties_allowed', true],
  ] as const) {
    rows.push(...compare(flag, before[flag] ?? fallback, after[flag] ?? fallback))
  }
  const left = patterns(before.patterns)
  const right = patterns(after.patterns)
  for (const key of new Set([...left.keys(), ...right.keys()]))
    rows.push(...compare(`patterns.${key}`, left.get(key), right.get(key)))
  return rows
}

export function diffValue(value: unknown): string {
  if (value === undefined) return '—'
  if (typeof value === 'string') return value
  if (!isRecord(value)) return String(value)
  return Object.entries(canonical(value) as Record<string, unknown>)
    .map(
      ([key, item]) => `${key}: ${typeof item === 'object' ? JSON.stringify(item) : String(item)}`
    )
    .join('\n')
}
